import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";

// Helper: agrupa por clave
function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    const k = item[key] || "SIN-CENTRO";
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});
}

export default function CostCentersPanel({ poId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!poId) return;

    let active = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const { data, error } = await supabase
          .from("v_service_orders_enriched")
          .select(
            `
            service_order_id,
            code,
            instrument_id,
            instrument_tag,
            instrument_brand,
            instrument_model,
            instrument_serial
          `
          )
          .eq("po_id", poId)
          .order("code", { ascending: true });

        if (!active) return;
        if (error) throw error;

        setRows(data || []);
      } catch (e) {
        console.error("[CostCentersPanel]", e);
        if (active) {
          setError(
            e.message || "No se pudieron cargar los Centros de Costo."
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [poId]);

  // Agrupar por SERV (Centro de Costo lógico)
  const grouped = useMemo(
    () => groupBy(rows, "code"),
    [rows]
  );

  const centerCodes = Object.keys(grouped);

  return (
    <div className="border border-white/10 rounded-xl bg-black/40 h-full flex flex-col">
      {/* HEADER */}
      <div className="px-4 py-3 border-b border-white/10">
        <div className="text-sm font-semibold">
          Centros de costo — OS existentes
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5">
          Instrumentos ya asignados a este CC
        </div>
      </div>

      {/* CONTENIDO */}
      <div className="flex-1 overflow-auto text-xs">
        {loading && (
          <div className="px-4 py-3 text-slate-400">
            Cargando centros de costo…
          </div>
        )}

        {!loading && error && (
          <div className="px-4 py-3 text-red-400">
            {error}
          </div>
        )}

        {!loading && !error && centerCodes.length === 0 && (
          <div className="px-4 py-3 text-slate-400">
            Esta OC todavía no tiene instrumentos asignados.
          </div>
        )}

        {!loading &&
          !error &&
          centerCodes.map((servCode) => {
            const items = grouped[servCode] || [];

            return (
              <div
                key={servCode}
                className="border-t border-white/10 px-4 py-3"
              >
                {/* CABECERA CC */}
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-sky-300">
                    {servCode}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-200 border border-sky-500/40">
                    {items.length}
                  </span>
                </div>

                {/* INSTRUMENTOS */}
                <div className="space-y-1">
                  {items.map((it) => {
                    const desc = [it.instrument_brand, it.instrument_model]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <div
                        key={it.instrument_id}
                        className="flex flex-col border border-white/5 rounded-md px-2 py-1 bg-black/30"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-slate-200">
                            {it.instrument_tag || "—"}
                          </span>
                          <span className="text-slate-400 text-[10px]">
                            {it.instrument_serial || ""}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {desc || "Sin descripción"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
